import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Radar, Trash2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import {
  getDeviceCameras, createDeviceCamera, updateDeviceCamera, deleteDeviceCamera, probeDeviceCameras,
  type DeviceCameraDto, type CameraLabelDto, type CameraProbeItemDto, type DeviceDto,
} from '../../lib/api/endpoints';
import { ApiError } from '../../lib/api/client';
import { useMpegtsPlayer } from '../../hooks/useMpegtsPlayer';
import { toHttpsStreamUrl } from '../../utils/streamUrl';
import { CAMERA_LABEL_LABELS } from '../../utils/constants';

interface DeviceCamerasModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: DeviceDto | null;
}

const CAMERA_LABEL_OPTIONS: { value: CameraLabelDto; label: string }[] =
  (Object.entries(CAMERA_LABEL_LABELS) as [CameraLabelDto, string][]).map(([value, label]) => ({ value, label }));

function describeApiError(err: unknown): string {
  if (err instanceof ApiError) return err.detail ? `${err.title}: ${err.detail}` : err.title;
  return err instanceof Error ? err.message : 'Error desconocido.';
}

// Previsualización de un canal sondeado. Silenciada a propósito (decisión
// laxa): son varios canales renderizándose a la vez en una grilla, y lo que
// importa aquí es la imagen, no el audio — a diferencia del reproductor de
// video en vivo operativo (`LiveVideoPlayer`), que sí exige audio activo por
// decisión de producto (EP-14-video-en-vivo.md §7).
function ProbeChannelPreview({ streamUrl }: { streamUrl: string }) {
  const { videoRef, error } = useMpegtsPlayer(toHttpsStreamUrl(streamUrl));
  return (
    <div style={{ position: 'relative', aspectRatio: '16/9', background: '#000', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
      <video ref={videoRef} controls muted style={{ width: '100%', height: '100%', display: error ? 'none' : 'block' }} />
      {error && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#FFF', fontSize: '11px', padding: '0.5rem', textAlign: 'center',
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

interface DeclareFormState {
  label: CameraLabelDto;
  name: string;
}

// FE-14 — declarar cámaras de un dispositivo (ADR-018). El mapa canal→cámara
// no es descubrible por la API del proveedor: el admin sondea, MIRA cada
// canal y declara solo lo que confirma haber visto.
export function DeviceCamerasModal({ isOpen, onClose, device }: DeviceCamerasModalProps) {
  const [cameras, setCameras] = useState<DeviceCameraDto[]>([]);
  const [isLoadingCameras, setIsLoadingCameras] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [isProbing, setIsProbing] = useState(false);
  const [probeItems, setProbeItems] = useState<CameraProbeItemDto[] | null>(null);
  const [probeError, setProbeError] = useState<string | null>(null);

  const [declareForms, setDeclareForms] = useState<Record<number, DeclareFormState>>({});
  const [declaringChannel, setDeclaringChannel] = useState<number | null>(null);
  const [declareError, setDeclareError] = useState<string | null>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const loadCameras = useCallback(async () => {
    if (!device) return;
    setIsLoadingCameras(true);
    setListError(null);
    try {
      const res = await getDeviceCameras(device.id);
      setCameras(res.items);
    } catch (err) {
      setListError(describeApiError(err));
    } finally {
      setIsLoadingCameras(false);
    }
  }, [device]);

  useEffect(() => {
    if (isOpen && device) {
      loadCameras();
      setProbeItems(null);
      setProbeError(null);
      setDeclareForms({});
    }
  }, [isOpen, device, loadCameras]);

  if (!device) return null;

  const declaredChannels = new Set(cameras.map(c => c.channel));

  const handleProbe = async () => {
    setIsProbing(true);
    setProbeError(null);
    try {
      const res = await probeDeviceCameras(device.id);
      setProbeItems(res.items);
    } catch (err) {
      setProbeError(describeApiError(err));
    } finally {
      setIsProbing(false);
    }
  };

  const handleDeclare = async (channel: number) => {
    const form = declareForms[channel] ?? { label: 'OTHER', name: '' };
    setDeclaringChannel(channel);
    setDeclareError(null);
    try {
      await createDeviceCamera(device.id, { channel, label: form.label, name: form.name || undefined, enabled: true });
      await loadCameras();
    } catch (err) {
      setDeclareError(describeApiError(err));
    } finally {
      setDeclaringChannel(null);
    }
  };

  const handleUpdateCamera = async (camera: DeviceCameraDto, patch: Partial<Pick<DeviceCameraDto, 'label' | 'name' | 'enabled'>>) => {
    setRowError(null);
    try {
      const updated = await updateDeviceCamera(device.id, camera.id, patch);
      setCameras(prev => prev.map(c => (c.id === camera.id ? updated : c)));
    } catch (err) {
      setRowError(describeApiError(err));
    }
  };

  const handleDelete = async (camera: DeviceCameraDto) => {
    if (!window.confirm(`¿Retirar "${camera.name || CAMERA_LABEL_LABELS[camera.label]}" del inventario? La novedad de sesiones no se borra.`)) return;
    setDeletingId(camera.id);
    setRowError(null);
    try {
      await deleteDeviceCamera(device.id, camera.id);
      setCameras(prev => prev.filter(c => c.id !== camera.id));
    } catch (err) {
      setRowError(describeApiError(err));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Cámaras — ${device.imei}`} size="lg">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

        {/* Cámaras ya declaradas */}
        <div>
          <h3 style={{ fontSize: '13px', fontWeight: 700, margin: '0 0 0.5rem', color: 'var(--text-secondary)' }}>
            Cámaras declaradas
          </h3>
          {isLoadingCameras ? (
            <Spinner size="sm" />
          ) : listError ? (
            <p style={{ fontSize: '12px', color: '#EF4444', margin: 0 }}>{listError}</p>
          ) : cameras.length === 0 ? (
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>
              Todavía no se ha declarado ninguna cámara de este dispositivo.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {cameras.map(c => (
                <div key={c.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
                  padding: '0.5rem 0.75rem', background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
                }}>
                  <span style={{ fontSize: '11px', fontFamily: 'monospace', color: 'var(--text-muted)', width: '52px' }}>
                    canal {c.channel}
                  </span>
                  <Select
                    aria-label="Etiqueta"
                    value={c.label}
                    onChange={e => handleUpdateCamera(c, { label: e.target.value as CameraLabelDto })}
                    options={CAMERA_LABEL_OPTIONS}
                    style={{ flex: '0 0 auto' }}
                  />
                  <Input
                    aria-label="Nombre"
                    placeholder="Nombre libre (opcional)"
                    defaultValue={c.name ?? ''}
                    onBlur={e => {
                      if ((c.name ?? '') !== e.target.value) handleUpdateCamera(c, { name: e.target.value || null });
                    }}
                    style={{ flex: '1 1 160px' }}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    <input type="checkbox" checked={c.enabled} onChange={e => handleUpdateCamera(c, { enabled: e.target.checked })} />
                    Habilitada
                  </label>
                  <Button variant="ghost" size="sm" leftIcon={<Trash2 size={13} />} isLoading={deletingId === c.id} onClick={() => handleDelete(c)}>
                    Retirar
                  </Button>
                </div>
              ))}
            </div>
          )}
          {rowError && <p style={{ fontSize: '12px', color: '#EF4444', margin: '0.5rem 0 0' }}>{rowError}</p>}
        </div>

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: 0 }} />

        {/* Sonda de canales */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <h3 style={{ fontSize: '13px', fontWeight: 700, margin: 0, color: 'var(--text-secondary)' }}>
              Sondear canales
            </h3>
            <Button variant="secondary" size="sm" leftIcon={<Radar size={14} />} isLoading={isProbing} onClick={handleProbe}>
              Sondear
            </Button>
          </div>

          <div style={{
            padding: '0.625rem 0.875rem', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.4)',
            borderRadius: 'var(--radius-md)', fontSize: '11.5px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '0.75rem',
          }}>
            <AlertTriangle size={13} style={{ verticalAlign: '-2px', marginRight: '4px', color: '#F59E0B' }} />
            Que un canal entregue video <strong>no prueba que haya cámara</strong> — el proveedor
            puede devolver barras de color. <strong>Dos canales pueden ser la misma cámara física</strong>:
            si ves la misma imagen en dos, declara solo una. Sondear <strong>puede desplazar la señal</strong>
            que alguien más esté viendo en la plataforma del proveedor para ese mismo canal. Y la
            sonda tarda — llama al proveedor canal por canal a propósito.
          </div>

          {isProbing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: '12px' }}>
              <Spinner size="sm" /> Sondeando canales, puede tardar…
            </div>
          )}

          {probeError && <p style={{ fontSize: '12px', color: '#EF4444', margin: 0 }}>{probeError}</p>}

          {probeItems && probeItems.length === 0 && (
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>La sonda no devolvió canales.</p>
          )}

          {probeItems && probeItems.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.875rem' }}>
              {probeItems.map(item => (
                <div key={item.channel} style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  <span style={{ fontSize: '11px', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                    canal {item.channel} {declaredChannels.has(item.channel) && '· ya declarado'}
                  </span>

                  {item.error ? (
                    <div style={{ aspectRatio: '16/9', background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: 'var(--text-muted)', padding: '0.5rem', textAlign: 'center' }}>
                      {item.error}
                    </div>
                  ) : item.streamUrl ? (
                    <ProbeChannelPreview streamUrl={item.streamUrl} />
                  ) : null}

                  {item.streamUrl && !declaredChannels.has(item.channel) && (
                    <div style={{ display: 'flex', gap: '0.375rem' }}>
                      <Select
                        aria-label="Etiqueta"
                        value={declareForms[item.channel]?.label ?? 'OTHER'}
                        onChange={e => setDeclareForms(prev => ({
                          ...prev,
                          [item.channel]: { label: e.target.value as CameraLabelDto, name: prev[item.channel]?.name ?? '' },
                        }))}
                        options={CAMERA_LABEL_OPTIONS}
                        style={{ flex: '0 0 auto' }}
                      />
                      <Input
                        aria-label="Nombre"
                        placeholder="Nombre (opcional)"
                        value={declareForms[item.channel]?.name ?? ''}
                        onChange={e => setDeclareForms(prev => ({
                          ...prev,
                          [item.channel]: { label: prev[item.channel]?.label ?? 'OTHER', name: e.target.value },
                        }))}
                        style={{ flex: '1 1 auto' }}
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        isLoading={declaringChannel === item.channel}
                        onClick={() => handleDeclare(item.channel)}
                      >
                        Declarar
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {declareError && <p style={{ fontSize: '12px', color: '#EF4444', margin: '0.5rem 0 0' }}>{declareError}</p>}
        </div>
      </div>
    </Modal>
  );
}
